import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Check, User, Lock, ClipboardCheck, Bell, Gift } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { t } from '@/i18n/translations';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const CATEGORIES = [
  { id: 'fitness', icon: '💪', key: 'fitness' as const, short: { nl: 'Fitness', fr: 'Fitness', en: 'Fitness' } },
  { id: 'sleep', icon: '😴', key: 'sleep' as const, short: { nl: 'Slaap', fr: 'Sommeil', en: 'Sleep' } },
  { id: 'mindfulness', icon: '🧘', key: 'mindfulness' as const, short: { nl: 'Mindful', fr: 'Mindful', en: 'Mindful' } },
  { id: 'productivity', icon: '⚡', key: 'productivity' as const, short: { nl: 'Productief', fr: 'Productif', en: 'Productive' } },
  { id: 'nutrition', icon: '🥗', key: 'nutrition' as const, short: { nl: 'Voeding', fr: 'Nutrition', en: 'Nutrition' } },
  { id: 'social', icon: '👥', key: 'social' as const, short: { nl: 'Sociaal', fr: 'Social', en: 'Social' } },
  { id: 'learning', icon: '📚', key: 'learning' as const, short: { nl: 'Leren', fr: 'Apprendre', en: 'Learn' } },
  { id: 'money', icon: '💰', key: 'money' as const, short: { nl: 'Geld', fr: 'Argent', en: 'Money' } },
  { id: 'cyber', icon: '🔒', key: 'cyber' as const, short: { nl: 'Beveiliging', fr: 'Sécurité', en: 'Security' } },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { settings } = useStore();
  const lang = settings.language;

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/onboarding?category=${categoryId}`);
  };

  const handleStartClick = () => {
    navigate('/onboarding');
  };

  return (
    <div className="min-h-screen flex flex-col pb-20">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Hero Section */}
      <section className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <CheckCircle2 className="w-10 h-10 text-primary-500" />
          <span className="text-2xl font-bold">
            <span className="text-primary-500">Easy</span>
            <span className="text-gray-800">Habits</span>
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          {t('landing.title', lang)}
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          {t('landing.subtitle', lang)}
        </p>

        {/* Features */}
        <div className="space-y-3 mb-8 text-left">
          <Feature icon={<ClipboardCheck className="w-5 h-5" />} text={t('landing.feature.track', lang)} />
          <Feature icon={<Bell className="w-5 h-5" />} text={t('landing.feature.nudge', lang)} />
          <Feature icon={<Gift className="w-5 h-5" />} text={t('landing.feature.free', lang)} />
          <Feature icon={<User className="w-5 h-5" />} text={t('landing.feature.noAccount', lang)} />
          <Feature icon={<Lock className="w-5 h-5" />} text={t('landing.feature.privacy', lang)} />
        </div>

        {/* CTA Button */}
        <button
          onClick={handleStartClick}
          className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-3 rounded-full shadow-lg transition-all hover:shadow-xl hover:scale-105"
        >
          {t('landing.cta', lang)} →
        </button>
      </section>

      {/* Categories Section */}
      <section className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-gray-800 text-center mb-6">
            {t('landing.question', lang)}
          </h2>

          <div className="grid grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm hover:shadow-md transition-all hover:scale-105 flex flex-col items-center gap-2"
              >
                <span className="text-3xl">{cat.icon}</span>
                <span className="text-sm font-medium text-gray-700">
                  {cat.short[lang]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-3 text-gray-700">
      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
        {icon}
      </div>
      <span>{text}</span>
    </div>
  );
}
